import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { getMonthNames } from '@/utils/date'

interface DateTimePickerProps {
  /** Current value (ms epoch). */
  value: number
  /** Lower bound — days before this are dimmed and unselectable. */
  min?: number
  /** Upper bound — days after this are dimmed and unselectable. */
  max?: number
  /** Optional raw event timestamps. Days containing at least one are
   *  marked with a small dot under the number. */
  timestamps?: number[]
  /** Header label, e.g. "Set start", "Set end". */
  label: string
  /** Called when the user clicks Apply; receives the chosen ms epoch. */
  onApply: (next: number) => void
  /** Called on Cancel or Esc. */
  onCancel: () => void
}

const DOW_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

/** Month names in the active UI language — never a hardcoded English list. */
function useMonthNames(style: 'long' | 'short'): string[] {
  const { i18n } = useTranslation()
  return useMemo(() => getMonthNames(i18n.language, style), [i18n.language, style])
}

function startOfDayMs(ms: number): number {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

function ymdKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** 6-row × 7-col grid of dates starting on Monday for the given month. */
function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1)
  // Monday-first: 0..6 where 0=Mon. JS getDay returns 0=Sun.
  const dow = (first.getDay() + 6) % 7
  const start = new Date(year, month, 1 - dow)
  const cells: Date[] = []
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i))
  }
  return cells
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo
  return Math.max(lo, Math.min(hi, n))
}

/** Last day of the given month — 28, 29, 30 or 31. */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

/** Owns the picker's draft state + month nav / day-select handlers. */
function usePickerState(value: number, min?: number, max?: number) {
  const initial = new Date(value)
  const [year, setYear] = useState(initial.getFullYear())
  const [month, setMonth] = useState(initial.getMonth())
  const [day, setDay] = useState(initial.getDate())
  const [hh, setHh] = useState(initial.getHours())
  const [mm, setMm] = useState(initial.getMinutes())

  const draft = useMemo(
    () => new Date(year, month, day, hh, mm, 0, 0).getTime(),
    [year, month, day, hh, mm]
  )
  const minDay = min !== undefined ? startOfDayMs(min) : null
  const maxDay = max !== undefined ? startOfDayMs(max) : null

  /**
   * Show a month, trimming the day to fit it: Jan 31 → Feb 28, never the
   * March 3 that `new Date(2026, 1, 31)` would silently roll over to.
   */
  const setYearMonth = (nextYear: number, nextMonth: number) => {
    setYear(nextYear)
    setMonth(nextMonth)
    setDay(current => Math.min(current, daysInMonth(nextYear, nextMonth)))
  }
  const goToMonth = (delta: number) => {
    const total = year * 12 + month + delta
    setYearMonth(Math.floor(total / 12), ((total % 12) + 12) % 12)
  }
  const selectDay = (d: Date) => {
    const ms = d.getTime()
    if ((minDay !== null && ms < minDay) || (maxDay !== null && ms > maxDay)) return
    setYear(d.getFullYear())
    setMonth(d.getMonth())
    setDay(d.getDate())
  }

  return {
    year,
    month,
    day,
    hh,
    mm,
    setHh,
    setMm,
    draft,
    minDay,
    maxDay,
    goToMonth,
    setYearMonth,
    selectDay,
  }
}

/** Bucket event timestamps by local-day key so DST transitions don't drop events. */
function useEventDays(timestamps: number[] | undefined): Set<string> {
  return useMemo(() => {
    const set = new Set<string>()
    if (timestamps) for (const t of timestamps) set.add(ymdKey(new Date(t)))
    return set
  }, [timestamps])
}

/**
 * Controls that already act on Enter themselves: a button activates (click) and
 * an input commits its text. Applying the draft from a picker-wide handler
 * would beat them to it and commit the value from *before* the keystroke.
 */
function ownsEnterKey(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && target.closest('button, input') !== null
}

/**
 * Picker-wide Escape / Enter shortcuts.
 *
 * Registered in the capture phase on `window`, which runs before the
 * `document` capture listener Radix's popover dismisses itself from. When
 * `onEscape` reports it consumed the key we stop propagation there, so the
 * first Escape closes only the month/year picker and the next one — which
 * `onEscape` declines — reaches the popover and closes it.
 */
function useEscEnterKeys(onEscape: () => boolean, onEnter: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (onEscape()) {
          e.preventDefault()
          e.stopPropagation()
        }
      } else if (e.key === 'Enter' && !ownsEnterKey(e.target)) {
        onEnter()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onEscape, onEnter])
}

export function DateTimePicker({
  value,
  min,
  max,
  timestamps,
  label,
  onApply,
  onCancel,
}: DateTimePickerProps) {
  const s = usePickerState(value, min, max)
  const eventDays = useEventDays(timestamps)
  const grid = useMemo(() => buildMonthGrid(s.year, s.month), [s.year, s.month])
  const todayKey = useMemo(() => ymdKey(new Date()), [])
  const [monthPickerOpen, setMonthPickerOpen] = useState(false)

  // Esc closes the month/year picker first, so it never discards the whole
  // draft. Only then does it become the popover's own dismiss key.
  const handleEscape = useCallback(() => {
    if (!monthPickerOpen) {
      onCancel()
      return false
    }
    setMonthPickerOpen(false)
    return true
  }, [monthPickerOpen, onCancel])
  const applyDraft = useCallback(() => onApply(s.draft), [onApply, s.draft])
  useEscEnterKeys(handleEscape, applyDraft)

  return (
    <div className="flex w-[300px] flex-col gap-2 font-sans text-foreground">
      <MonthNav
        label={label}
        cursor={{ month: s.month, year: s.year }}
        monthPicker={{
          open: monthPickerOpen,
          onToggle: () => setMonthPickerOpen(open => !open),
        }}
        onStep={s.goToMonth}
      />
      <div className="h-px bg-border" />
      {monthPickerOpen ? (
        <MonthYearGrid
          month={s.month}
          year={s.year}
          onSelect={(nextYear, nextMonth) => {
            s.setYearMonth(nextYear, nextMonth)
            setMonthPickerOpen(false)
          }}
        />
      ) : (
        <CalendarGrid
          grid={grid}
          month={s.month}
          year={s.year}
          day={s.day}
          eventDays={eventDays}
          todayKey={todayKey}
          minDay={s.minDay}
          maxDay={s.maxDay}
          onSelectDay={s.selectDay}
        />
      )}
      <div className="h-px bg-border" />
      <FooterActions
        hh={s.hh}
        mm={s.mm}
        onHourChange={n => s.setHh(clamp(n, 0, 23))}
        onMinuteChange={n => s.setMm(clamp(n, 0, 59))}
        onApply={() => onApply(s.draft)}
        onCancel={onCancel}
      />
    </div>
  )
}

interface MonthNavProps {
  label: string
  /** Month/year the calendar is currently showing. */
  cursor: { month: number; year: number }
  /** Open state + toggle of the month/year quick picker. */
  monthPicker: { open: boolean; onToggle: () => void }
  /** Step the calendar by `delta` months (−1 / +1 from the chevrons). */
  onStep: (delta: number) => void
}

/** Eyebrow label + month chevrons row. The headline opens the quick picker. */
function MonthNav({ label, cursor, monthPicker, onStep }: MonthNavProps) {
  const { t } = useTranslation()
  const monthNames = useMonthNames('long')
  const headline = t('timeRange.picker.monthYear', {
    month: monthNames[cursor.month],
    year: cursor.year,
  })

  return (
    <div className="flex items-center justify-between px-1">
      <div className="flex items-center gap-2">
        <span className="inline-block h-1.5 w-1.5 rotate-45 bg-primary" aria-hidden />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="flex items-center gap-0.5">
        <NavArrowButton
          direction="prev"
          title={t('timeRange.picker.previousMonth')}
          onClick={() => onStep(-1)}
        />
        <button
          type="button"
          onClick={monthPicker.onToggle}
          aria-expanded={monthPicker.open}
          title={t('timeRange.picker.openMonthYear')}
          className={cn(
            'min-w-[112px] rounded px-1 py-2 text-center font-mono text-[11px] tracking-[0.04em] tabular-nums transition-colors md:py-0.5',
            monthPicker.open
              ? 'bg-accent text-foreground'
              : 'text-foreground hover:bg-accent hover:text-foreground'
          )}
        >
          {headline}
        </button>
        <NavArrowButton
          direction="next"
          title={t('timeRange.picker.nextMonth')}
          onClick={() => onStep(1)}
        />
      </div>
    </div>
  )
}

/** Chevron stepper used by both the month row and the year row. */
function NavArrowButton({
  direction,
  title,
  onClick,
}: {
  direction: 'prev' | 'next'
  title: string
  onClick: () => void
}) {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:h-6 md:w-6"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

interface MonthYearGridProps {
  /** Month currently shown by the calendar (0-11) — highlighted when its year is browsed. */
  month: number
  /** Year currently shown by the calendar; the picker opens on it. */
  year: number
  /** Commit a month/year jump. Closes the picker. */
  onSelect: (year: number, month: number) => void
}

/** Quick month/year jump: year stepper + 3×4 month grid, one click per jump. */
function MonthYearGrid({ month, year, onSelect }: MonthYearGridProps) {
  const { t } = useTranslation()
  const monthNames = useMonthNames('long')
  const shortNames = useMonthNames('short')
  // Local browse cursor: stepping the year must not move the calendar until
  // a month is picked. Remounted (and so reset) every time the picker opens.
  const [browseYear, setBrowseYear] = useState(year)

  return (
    <div className="flex min-h-[276px] flex-col gap-2">
      <div className="flex items-center justify-between px-1">
        <NavArrowButton
          direction="prev"
          title={t('timeRange.picker.previousYear')}
          onClick={() => setBrowseYear(y => y - 1)}
        />
        <span className="font-mono text-[11px] tracking-[0.04em] tabular-nums text-foreground">
          {browseYear}
        </span>
        <NavArrowButton
          direction="next"
          title={t('timeRange.picker.nextYear')}
          onClick={() => setBrowseYear(y => y + 1)}
        />
      </div>
      <div className="grid flex-1 grid-cols-3 grid-rows-4 gap-1">
        {monthNames.map((name, i) => {
          const isCurrent = i === month && browseYear === year
          return (
            <button
              key={name}
              type="button"
              onClick={() => onSelect(browseYear, i)}
              aria-label={t('timeRange.picker.monthYear', { month: name, year: browseYear })}
              aria-current={isCurrent ? 'true' : undefined}
              className={cn(
                'rounded-sm font-mono text-[11px] uppercase tracking-[0.08em] transition-colors',
                isCurrent
                  ? 'bg-primary font-medium text-primary-foreground shadow-sm'
                  : 'text-foreground hover:bg-accent'
              )}
            >
              {shortNames[i]}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface CalendarGridProps {
  grid: Date[]
  month: number
  year: number
  day: number
  eventDays: Set<string>
  todayKey: string
  minDay: number | null
  maxDay: number | null
  onSelectDay: (d: Date) => void
}

/** Monday-first 7×6 day grid with selection, today ring, and event dots. */
function CalendarGrid({
  grid,
  month,
  year,
  day,
  eventDays,
  todayKey,
  minDay,
  maxDay,
  onSelectDay,
}: CalendarGridProps) {
  return (
    <div className="grid grid-cols-7 gap-0.5">
      {DOW_LABELS.map((l, i) => (
        <span
          key={`dow-${i}`}
          className="py-1 text-center font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground/60"
        >
          {l}
        </span>
      ))}
      {grid.map(d => (
        <DayCell
          key={d.getTime()}
          d={d}
          inMonth={d.getMonth() === month}
          isSelected={d.getFullYear() === year && d.getMonth() === month && d.getDate() === day}
          isToday={ymdKey(d) === todayKey}
          hasEvents={eventDays.has(ymdKey(d))}
          outOfBounds={
            (minDay !== null && d.getTime() < minDay) || (maxDay !== null && d.getTime() > maxDay)
          }
          onClick={() => onSelectDay(d)}
        />
      ))}
    </div>
  )
}

interface DayCellProps {
  d: Date
  inMonth: boolean
  isSelected: boolean
  isToday: boolean
  hasEvents: boolean
  outOfBounds: boolean
  onClick: () => void
}

function dayCellClass({ inMonth, isSelected, isToday, outOfBounds }: DayCellProps): string {
  return cn(
    'relative aspect-square rounded-sm font-mono text-[11px] tabular-nums transition-colors',
    outOfBounds && 'cursor-not-allowed text-muted-foreground/25',
    !outOfBounds &&
      !isSelected &&
      (inMonth ? 'text-foreground hover:bg-accent' : 'text-muted-foreground/40 hover:bg-accent/50'),
    isSelected && 'bg-primary text-primary-foreground font-medium shadow-sm',
    isToday && !isSelected && 'ring-1 ring-inset ring-primary/60'
  )
}

function DayCell(props: DayCellProps) {
  const { d, isSelected, hasEvents, outOfBounds, onClick } = props
  return (
    <button type="button" onClick={onClick} disabled={outOfBounds} className={dayCellClass(props)}>
      <span>{d.getDate()}</span>
      {hasEvents && !outOfBounds && (
        <span
          className={cn(
            'absolute bottom-1 left-1/2 h-[3px] w-[3px] -translate-x-1/2 rounded-full',
            isSelected ? 'bg-primary-foreground/80' : 'bg-primary/80'
          )}
          aria-hidden
        />
      )}
    </button>
  )
}

interface FooterActionsProps {
  hh: number
  mm: number
  onHourChange: (n: number) => void
  onMinuteChange: (n: number) => void
  onApply: () => void
  onCancel: () => void
}

/** Time stepper row + Cancel/Apply buttons. Commits via `onApply`. */
function FooterActions({
  hh,
  mm,
  onHourChange,
  onMinuteChange,
  onApply,
  onCancel,
}: FooterActionsProps) {
  return (
    <>
      <div className="flex items-center justify-between px-1 pt-0.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Time
        </span>
        <div className="flex items-center gap-1">
          <NumberStepper value={hh} onChange={onHourChange} min={0} max={23} label="hours" />
          <span className="text-foreground">:</span>
          <NumberStepper value={mm} onChange={onMinuteChange} min={0} max={59} label="minutes" />
        </div>
      </div>
      <div className="h-px bg-border" />
      <div className="flex items-center justify-end gap-1.5 px-1 pt-0.5">
        <FooterButton onClick={onCancel} variant="ghost" label="Cancel" />
        <FooterButton onClick={onApply} variant="primary" label="Apply" />
      </div>
    </>
  )
}

function FooterButton({
  onClick,
  variant,
  label,
}: {
  onClick: () => void
  variant: 'ghost' | 'primary'
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] max-md:px-3.5 max-md:py-2',
        variant === 'primary'
          ? 'bg-primary px-2.5 text-primary-foreground shadow-sm hover:bg-primary/90'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      )}
    >
      {label}
    </button>
  )
}

interface StepperProps {
  value: number
  onChange: (next: number) => void
  min: number
  max: number
  label: string
}

function wrap(value: number, dir: 1 | -1, min: number, max: number): number {
  if (dir === 1) return value >= max ? min : value + 1
  return value <= min ? max : value - 1
}

/** Two-digit stepper with type-to-edit + ▲▼ buttons. Wraps around at min/max. */
function NumberStepper({ value, onChange, min, max, label }: StepperProps) {
  const [text, setText] = useState(() => String(value).padStart(2, '0'))
  // Re-sync display when value changes externally (e.g. day click).
  useEffect(() => setText(String(value).padStart(2, '0')), [value])

  const bump = (dir: 1 | -1) => onChange(wrap(value, dir, min, max))
  const commit = (raw: string) => {
    const n = Number.parseInt(raw, 10)
    const next = Number.isNaN(n) ? value : clamp(n, min, max)
    onChange(next)
    setText(String(next).padStart(2, '0'))
  }
  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
    else if (e.key === 'ArrowUp') bump(1)
    else if (e.key === 'ArrowDown') bump(-1)
    else return
    e.preventDefault()
  }

  return (
    <div className="inline-flex h-9 items-stretch overflow-hidden rounded-sm border border-border bg-card md:h-7">
      <input
        aria-label={label}
        value={text}
        onChange={e => setText(e.target.value.replace(/\D/g, '').slice(0, 2))}
        onBlur={e => commit(e.target.value)}
        onKeyDown={handleKey}
        // 16px text on a phone: anything smaller makes iOS zoom the page on focus.
        className="w-8 bg-transparent px-1 text-center font-mono text-[12px] tabular-nums text-foreground outline-none max-md:w-10 max-md:text-base"
        inputMode="numeric"
      />
      <div className="flex flex-col border-l border-border">
        <StepperArrow direction="up" onClick={() => bump(1)} />
        <StepperArrow direction="down" onClick={() => bump(-1)} />
      </div>
    </div>
  )
}

function StepperArrow({ direction, onClick }: { direction: 'up' | 'down'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      tabIndex={-1}
      className="flex h-[17px] w-8 items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground md:h-3.5 md:w-4"
    >
      <span className="-mt-px text-[8px] leading-none">{direction === 'up' ? '▲' : '▼'}</span>
    </button>
  )
}
